import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export const exportToPDF = async (filename: string = 'waymining-landing-page', elementId: string = 'landing-page-container') => {
  try {
    // Scroll to top before capturing
    window.scrollTo(0, 0);

    // Wait a moment for animations to settle
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Get the main container element
    const element = document.getElementById(elementId) || document.body;
    
    // Temporarily hide floating elements
    const elementsToHide = document.querySelectorAll('.share-menu, .pdf-export-button, .presentation-btn-floating');
    const originalDisplays: string[] = [];
    elementsToHide.forEach((el, index) => {
      originalDisplays[index] = (el as HTMLElement).style.display;
      (el as HTMLElement).style.display = 'none';
    });
    
    // Wait for all images to load
    await new Promise(resolve => {
      const images = element.querySelectorAll('img');
      let loadedCount = 0;
      const totalImages = images.length;
      
      if (totalImages === 0) {
        resolve(void 0);
        return;
      }
      
      images.forEach(img => {
        if (img.complete) {
          loadedCount++;
          if (loadedCount === totalImages) resolve(void 0);
        } else {
          img.onload = img.onerror = () => {
            loadedCount++;
            if (loadedCount === totalImages) resolve(void 0);
          };
        }
      });
      
      // Timeout after 10 seconds
      setTimeout(() => resolve(void 0), 10000);
    });
    
    // Configure html2canvas for proper A4 PDF
    const canvas = await html2canvas(element, {
      useCORS: true,
      allowTaint: false,
      scale: 2, // Higher scale for better readability
      backgroundColor: '#0a0b0d',
      scrollX: 0,
      scrollY: 0,
      width: element.scrollWidth,
      height: element.scrollHeight,
      foreignObjectRendering: false,
      removeContainer: true,
      logging: false,
      imageTimeout: 15000,
      onclone: (clonedDoc) => {
        // Hide floating elements in clone
        const clonedElements = clonedDoc.querySelectorAll('.share-menu, .pdf-export-button, .presentation-btn-floating');
        clonedElements.forEach(el => {
          (el as HTMLElement).style.display = 'none';
        });
        
        // Ensure all images are loaded
        const clonedImages = clonedDoc.querySelectorAll('img');
        clonedImages.forEach(img => {
          if (img.src && !img.complete) {
            img.loading = 'eager';
          }
        });
      }
    });
    
    // Restore hidden elements
    elementsToHide.forEach((el, index) => {
      (el as HTMLElement).style.display = originalDisplays[index];
    });

    const imgData = canvas.toDataURL('image/png', 0.95);
    
    // Use full width and let height be dynamic for the long content
    const pdfWidth = 210; // A4 width
    const margin = 5; // Very small margin
    const availableWidth = pdfWidth - (margin * 2);
    
    const imgAspectRatio = canvas.height / canvas.width;
    
    // Make content use full width
    const finalWidth = availableWidth;
    const finalHeight = availableWidth * imgAspectRatio;
    
    // Create PDF with custom height to fit all content at full width
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [pdfWidth, finalHeight + (margin * 2)] // Custom height to fit content
    });
    
    // Add the image to PDF - uses full width, no compression
    pdf.addImage(
      imgData, 
      'PNG', 
      margin, 
      margin, 
      finalWidth, 
      finalHeight, 
      undefined, 
      'FAST'
    );
    
    // Save the PDF
    pdf.save(`${filename}.pdf`);
    
    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return false;
  }
};

export const exportSectionToPDF = async (sectionId: string, filename: string) => {
  try {
    const element = document.getElementById(sectionId);
    if (!element) {
      console.error(`Element with ID "${sectionId}" not found`);
      return false;
    }
    
    // Store original layout
    const originalWidth = document.body.style.width;
    
    // Force mobile layout for section
    document.body.style.width = '375px';
    document.documentElement.style.width = '375px';
    window.dispatchEvent(new Event('resize'));
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const canvas = await html2canvas(element, {
      useCORS: true,
      scale: 3,
      backgroundColor: '#0a0b0d',
      allowTaint: false,
      foreignObjectRendering: false,
      width: 375,
      height: element.scrollHeight,
      imageTimeout: 15000,
    });
    
    // Restore original layout
    document.body.style.width = originalWidth;
    document.documentElement.style.width = '';
    window.dispatchEvent(new Event('resize'));
    
    const imgData = canvas.toDataURL('image/png', 0.95);
    const pdfWidth = 105; // Mobile-friendly width
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [pdfWidth, imgHeight]
    });
    
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight, undefined, 'FAST');
    pdf.save(`${filename}.pdf`);
    
    return true;
  } catch (error) {
    console.error('Error generating section PDF:', error);
    return false;
  }
};